<template>
  <div class="page-stack">
    <section class="section-shell">
      <div class="section-head">
        <div>
          <div class="section-kicker">Overview</div>
          <h3 class="section-title">运行概览</h3>
          <p class="section-subtitle">重点数据、操作顺序和系统说明统一放在同一个视图里。</p>
        </div>
      </div>
    </section>

    <section class="stat-grid">
      <article
        v-for="card in cards"
        :key="card.label"
        class="stat-tile"
      >
        <div class="stat-tile__label">{{ card.label }}</div>
        <div class="stat-tile__value">{{ card.value }}</div>
        <div class="stat-tile__desc">{{ card.desc }}</div>
      </article>
    </section>

    <section class="section-shell">
      <div class="section-head">
        <div>
          <div class="section-kicker">Workflow</div>
          <h3 class="section-title">运行步骤列表</h3>
          <p class="section-subtitle">整个系统按列表方式组织，先看步骤，再进入各模块维护数据。</p>
        </div>
      </div>
      <div class="table-shell mt-6 overflow-auto">
        <table class="data-table">
          <thead>
            <tr>
              <th>步骤</th>
              <th>模块</th>
              <th>说明</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="item in workflow" :key="item.step">
              <td class="font-semibold text-brand-teal">{{ item.step }}</td>
              <td class="font-semibold text-slate-900">{{ item.title }}</td>
              <td class="text-sm leading-6 text-slate-600">{{ item.desc }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>

    <section class="section-shell">
      <div class="section-head">
        <div>
          <div class="section-kicker">Notes</div>
          <h3 class="section-title">系统说明</h3>
        </div>
      </div>
      <div class="note-grid mt-6">
        <article class="note-card">
          <div class="note-card__title">产品记录</div>
          <p class="note-card__text">产品记录会持久化保存，图片上传后会转为资产并持续绑定到产品。</p>
        </article>
        <article class="note-card">
          <div class="note-card__title">Gemini 回退</div>
          <p class="note-card__text">Gemini 未配置时，系统会使用本地回退文案，确保你可以先测试完整流程。</p>
        </article>
        <article class="note-card">
          <div class="note-card__title">Liblib 调用</div>
          <p class="note-card__text">Liblib 未接入准确接口时会走本地预览复制模式，便于先验证上传、绑定和日志链路。</p>
        </article>
      </div>
    </section>
  </div>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue';
import { dashboardApi } from '@/api';
import { notifyError } from '@/utils/feedback';

const summary = ref({
  totalProducts: 0,
  totalUsers: 0,
  totalPrompts: 0,
  totalImages: 0
});

const workflow = [
  {
    step: '01',
    title: '先维护提示词模板',
    desc: '为图片改色和产品描述分别准备可复用模板，后续选择后仍可微调。'
  },
  {
    step: '02',
    title: '新建产品并上传原图',
    desc: '录入 Etsy 模板字段，上传多张源图，保存后会生成产品记录和图片资产关联。'
  },
  {
    step: '03',
    title: '生成描述与图片',
    desc: '先跑描述，再跑图片任务，最后批量勾选导出 CSV。'
  }
];

const cards = computed(() => [
  { label: '产品数', value: summary.value.totalProducts, desc: '已保存的产品记录' },
  { label: '用户数', value: summary.value.totalUsers, desc: '包含管理员与运营用户' },
  { label: '提示词数', value: summary.value.totalPrompts, desc: '可复用的模板资产' },
  { label: '图片数', value: summary.value.totalImages, desc: '上传与生成的图片总量' }
]);

async function loadSummary() {
  try {
    const res = await dashboardApi.summary();
    summary.value = res.data;
  } catch (error) {
    notifyError(error.message || '加载仪表盘失败');
  }
}

onMounted(loadSummary);
</script>
